/**
 * Finding your way in a long conversation: the dot rail on the right edge (one dot per
 * question, the one you are reading ringed; hover lists them, a click jumps), find in this
 * conversation (Ctrl+F: "3 of 11", Enter for the next, Shift+Enter for the previous), and the
 * keyboard shortcuts.
 */
import { ChevronDown, ChevronUp, Search, X } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Dialog, IconButton, Input, Kbd, Popover, PopoverAnchor, PopoverContent, Pressable, cn, formatClock, formatInt } from '@orgus/ui';
import type { Turn } from './turns.ts';

// ---------------------------------------------------------------------------
// Question rail
// ---------------------------------------------------------------------------

/** Rails with more questions than this spread this many dots evenly. */
const MAX_DOTS = 30;

function scrollToTurn(scroller: HTMLElement | null, key: string) {
  const el = scroller?.querySelector<HTMLElement>(`[data-turn="${CSS.escape(key)}"]`);
  if (!el || !scroller) return;
  const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 8;
  scroller.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  el.classList.add('bg-hover');
  setTimeout(() => el.classList.remove('bg-hover'), 1100);
}

/** The question being read: the last one whose top is above the middle of the view. */
function useActiveTurn(scroller: RefObject<HTMLElement | null>, keys: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const read = () => {
      const mid = el.getBoundingClientRect().top + el.clientHeight / 3;
      let cur: string | null = keys[0] ?? null;
      for (const k of keys) {
        const node = el.querySelector(`[data-turn="${CSS.escape(k)}"]`);
        if (node && node.getBoundingClientRect().top <= mid) cur = k;
      }
      setActive(cur);
    };
    read();
    el.addEventListener('scroll', read, { passive: true });
    return () => el.removeEventListener('scroll', read);
  }, [scroller, keys]);
  return active;
}

export function QuestionRail({ turns, scroller }: { turns: Turn[]; scroller: RefObject<HTMLElement | null> }) {
  const questions = useMemo(() => turns.filter((t) => t.user), [turns]);
  const keys = useMemo(() => questions.map((t) => t.key), [questions]);
  const active = useActiveTurn(scroller, keys);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  if (questions.length < 3) return null;
  const dots = questions.length > MAX_DOTS ? Array.from({ length: MAX_DOTS }, (_, i) => questions[Math.round((i * (questions.length - 1)) / (MAX_DOTS - 1))]!) : questions;
  const hover = (on: boolean) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(on), on ? 120 : 220);
  };
  const found = questions.filter((t) => !q.trim() || `${t.user!.text ?? ''} ${t.answer}`.toLowerCase().includes(q.trim().toLowerCase()));
  const jump = (key: string) => {
    scrollToTurn(scroller.current, key);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <nav
          aria-label={`Questions in this conversation (${questions.length})`}
          className="absolute right-3 top-1/2 z-(--z-sticky) hidden -translate-y-1/2 flex-col items-center rounded-pill py-2 hover:bg-hover lg:flex"
          onMouseEnter={() => hover(true)}
          onMouseLeave={() => hover(false)}
        >
          {dots.map((t) => (
            <Pressable key={t.key} onClick={() => jump(t.key)} aria-label={`Go to: ${(t.user!.text ?? '').slice(0, 80)}`} aria-current={t.key === active || undefined} className="flex h-5 w-3 items-center justify-center">
              <span className={cn('rounded-pill transition-colors duration-(--duration-fast)', t.key === active ? 'size-2 border border-accent' : 'size-1.5 bg-border-strong')} />
            </Pressable>
          ))}
        </nav>
      </PopoverAnchor>
      <PopoverContent side="left" align="center" className="w-80 p-2" onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)} onOpenAutoFocus={(e) => e.preventDefault()}>
        <Input aria-label="Search this conversation" placeholder="Search this conversation" size="sm" value={q} onChange={(e) => setQ(e.target.value)} icon={<Search />} />
        <ul className="mt-2 flex max-h-96 flex-col overflow-y-auto">
          {found.length ? (
            found.map((t) => (
              <li key={t.key}>
                <Pressable onClick={() => jump(t.key)} className={cn('flex w-full items-start gap-2 rounded-control px-2 py-2 text-left hover:bg-hover', t.key === active && 'bg-active')}>
                  <span className={cn('line-clamp-2 min-w-0 flex-1 text-ui', t.key === active ? 'text-fg' : 'text-fg-muted')}>{t.user!.text}</span>
                  <span className="shrink-0 text-caption text-fg-subtle num">{formatClock(t.user!.at)}</span>
                </Pressable>
              </li>
            ))
          ) : (
            <li className="px-2 py-2 text-ui text-fg-muted">Nothing in this conversation matches “{q.trim()}”.</li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Find
// ---------------------------------------------------------------------------

export function FindBar({ turns, scroller, onClose }: { turns: Turn[]; scroller: RefObject<HTMLElement | null>; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const needle = q.trim().toLowerCase();
  const matches = useMemo(
    () => (needle.length < 2 ? [] : turns.filter((t) => `${t.user?.text ?? ''}\n${t.answer}\n${t.rounds.map((r) => r.narration).join('\n')}`.toLowerCase().includes(needle)).map((t) => t.key)),
    [turns, needle],
  );
  useEffect(() => input.current?.focus(), []);
  useEffect(() => setAt(0), [needle]);
  useEffect(() => {
    const key = matches[at];
    const el = key ? scroller.current?.querySelector<HTMLElement>(`[data-turn="${CSS.escape(key)}"]`) : null;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // An outline outside the turn: it never covers the words it marks.
    el.classList.add('outline-2', 'outline-offset-4', 'outline-accent', 'rounded-card');
    return () => el.classList.remove('outline-2', 'outline-offset-4', 'outline-accent', 'rounded-card');
  }, [matches, at, scroller]);
  const step = (d: 1 | -1) => matches.length && setAt((a) => (a + d + matches.length) % matches.length);
  return (
    <div role="search" className="flex h-filter-bar shrink-0 items-center gap-2 border-b border-border-subtle px-4">
      <Input
        ref={input}
        aria-label="Find in this conversation"
        placeholder="Find in this conversation"
        size="sm"
        icon={<Search />}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            step(e.shiftKey ? -1 : 1);
          }
          if (e.key === 'Escape') onClose();
        }}
        wrapperClassName="min-w-0 max-w-sm flex-1"
      />
      <span className="text-caption text-fg-subtle num" aria-live="polite">
        {needle.length < 2 ? '' : matches.length ? `${formatInt(at + 1)} of ${formatInt(matches.length)}` : `Nothing here says “${q.trim()}”`}
      </span>
      <IconButton size="xs" label="Previous match" icon={<ChevronUp />} disabled={!matches.length} onClick={() => step(-1)} shortcut={['shift', 'enter']} />
      <IconButton size="xs" label="Next match" icon={<ChevronDown />} disabled={!matches.length} onClick={() => step(1)} shortcut={['enter']} />
      <IconButton size="xs" label="Close find" icon={<X />} onClick={onClose} shortcut={['esc']} className="ml-auto" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shortcuts
// ---------------------------------------------------------------------------

export const SHORTCUTS: { group: string; keys: string[]; what: string }[] = [
  { group: 'Conversations', keys: ['mod', 'k'], what: 'Search conversations' },
  { group: 'Conversations', keys: ['mod', 'shift', 'o'], what: 'New conversation' },
  { group: 'Conversations', keys: ['mod', 'f'], what: 'Find in this conversation' },
  { group: 'Conversations', keys: ['mod', '/'], what: 'Keyboard shortcuts' },
  { group: 'Writing', keys: ['enter'], what: 'Send' },
  { group: 'Writing', keys: ['shift', 'enter'], what: 'New line' },
  { group: 'Writing', keys: ['up'], what: 'Edit your last message (empty box)' },
  { group: 'Writing', keys: ['esc'], what: 'Stop the reply' },
  { group: 'Writing', keys: ['/'], what: 'Write a message' },
];

export function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const groups = [...new Set(SHORTCUTS.map((s) => s.group))];
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Keyboard shortcuts" size="sm">
      <div className="flex flex-col gap-4">
        {groups.map((g) => (
          <section key={g} className="flex flex-col gap-1">
            <h3 className="text-caption font-medium text-fg-subtle">{g}</h3>
            <dl className="flex flex-col">
              {SHORTCUTS.filter((s) => s.group === g).map((s) => (
                <div key={s.what} className="flex h-row items-center justify-between gap-4 text-ui">
                  <dt className="text-fg">{s.what}</dt>
                  <dd>
                    <Kbd keys={s.keys} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
